/**
 * Hybrid Button Component
 * Automatically chooses between Tailwind and styled-components based on props
 */

import React from "react";
import type { TypeButtonProps } from "./ButtonTypes";
import Container from "./styles"; // Styled-components version
import { ButtonTailwind } from "./ButtonTailwind"; // Tailwind version
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";

const ButtonHybrid = React.forwardRef<HTMLButtonElement, TypeButtonProps>(
  (props, ref) => {
    const {
      href,
      external,
      innerRef,
      onClick,
      appearance,
      disabled,
      fullWidth,
      "aria-disabled": nativeAriaDisabled,
      ...rest
    } = props;

    if (!href && external) {
      // eslint-disable-next-line no-console
      console.warn(
        "Warning: external prop cannot be set without a href declaration"
      );
    }

    // AI appearances rely on a gradient fill and a masked gradient border that
    // the styled-components path can't reproduce, so they always render via the
    // Tailwind/button.css path. Trade-off: system-prop styling (mt, mb, …) is not
    // supported on AI variants, consistent with the Tailwind-only direction.
    const isAi = appearance?.startsWith("ai-") ?? false;

    if (!isAi && hasStyledProps(rest)) {
      return (
        <Container
          title={props.title}
          active={props.active}
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          as={props.as || (href ? "a" : "button")}
          type={href ? undefined : "button"}
          // Use aria-disabled instead of the HTML disabled attribute: a native `disabled`
          // removes the button from the tab order AND suppresses hover events that
          // disabled-button tooltips rely on. The click is already JS-guarded below.
          // Merged like aria-label below: an explicit aria-disabled prop is honored,
          // but disabled={true} always forces "true" regardless of what was passed.
          aria-disabled={disabled ? disabled : nativeAriaDisabled}
          buttonSize={props.size ?? "default"}
          appearance={appearance || "unstyled"}
          fullWidth={fullWidth}
          ref={ref || innerRef}
          onClick={
            disabled
              ? (e: React.MouseEvent) => {
                  e.preventDefault();
                }
              : onClick
          }
          data-qa-button={props.title || ""}
          data-qa-button-isdisabled={disabled === true}
          aria-label={props.ariaLabel}
          {...props.qa}
          {...rest}
        >
          {props.children}
        </Container>
      );
    }

    // Use Tailwind version (preferred - better performance)
    return <ButtonTailwind {...props} ref={ref || innerRef} />;
  }
);

ButtonHybrid.displayName = "Button";
export default ButtonHybrid;
